import { Config } from '@src/core/Config';
import { difficulty } from '@src/core/Difficulty';
import { ARCHMAGE_COUNT, BossKind, EnemyKind, SpriteId } from '@src/ecs/components';
import type { Rng } from '@src/core/Rng';

/** What the boss is made of: drives hurt takes and death crescendo timbre. */
export enum BossBody {
  Arcane = 0,
  Stone = 1,
  Bone = 2,
  Goo = 3,
  Leather = 4,
}

/** How the boss shows up after the rune circle ignites. */
export enum BossEntrance {
  /** Materialises out of rune light (archmages, Skull King). */
  Blink = 0,
  /** Falls from above and craters the floor (Warlord). */
  Crash = 1,
  /** Pushes up through the flagstones (Colossus, Sentinel). */
  Rise = 2,
  /** Swoops in over the wall (Matriarch). */
  Swoop = 3,
}

/** Locomotion between casts. */
export enum BossMotion {
  /** Drifts at standoff range and blinks when crowded (archmages). */
  Hover = 0,
  /** Lumbers straight at the wizard. */
  Walk = 1,
  /** Hops in short bounds. */
  Hop = 2,
  /** Fast circling flyer. */
  Flyer = 3,
  /** Rooted in place. */
  Static = 4,
}

export interface BossDef {
  kind: BossKind;
  name: string;
  /** Short title for the HUD bar. */
  title: string;
  sprite: SpriteId;
  /** Announcer group played on arrival. */
  voiceGroup: string;
  /** Spell palette: bolt core, orb, wisp, telegraph glow, death burst. */
  boltTint: number;
  orbTint: number;
  wispTint: number;
  glowTint: number;
  /** Floor decal color left where the boss dies. */
  stainTint: number;
  /** Multiplied into the sprite (0xffffff = the sheet's own colours). */
  spriteTint: number;
  /** True for the exit-room archmages; false for room champions. */
  archmage: boolean;
  body: BossBody;
  entrance: BossEntrance;
  motion: BossMotion;
  /** Sprite scale (world units tall) and collision radius. */
  scale: number;
  radius: number;
  /** Hover height for flyers (0 = grounded). */
  lift: number;
  /** Locomotion speed in wu/s (0 for static). */
  speed: number;
  /** HP multiplier over the class base (champion or archmage). */
  hpMult: number;
  /** Pattern set per escalation tier (tier 0 = first rooms). */
  patterns: readonly (readonly BossPattern[])[];
  /** Small fry this boss splits into or calls (-1 = none). */
  minion: EnemyKind | -1;
  /** Floor theme it is biased toward (0 obsidian, 1 verdant, 2 infernal). */
  theme: number;
  /** Champions only: first floor this boss can be drawn on (default 1). */
  debutFloor?: number;
}

/** Spell patterns; the unlocked set grows by floor/room. */
export enum BossPattern {
  /** Ring of rune bolts around the boss, rotating offset per volley. */
  Radial = 0,
  /** Fast fan aimed at the wizard. */
  Aimed = 1,
  /** Orbs circle the boss, then all fly at the wizard. */
  Orbit = 2,
  /** Slow expanding ring with a gap to slip through. */
  RingGap = 3,
  /** Slow homing wisps that follow the wizard. */
  Wisps = 4,
  /** Telegraphed beam that sweeps across the room. */
  Beam = 5,
  /** Warlord: hammer slam, expanding shockwave rings that shove. */
  Stomp = 6,
  /** Warlord / Matriarch: locked-direction charge or dive across the room. */
  Charge = 7,
  /** Warlord: fan of hurled rubble that tumbles and slows. */
  Rubble = 8,
  /** Skull King: crown of skulls orbits, then launches one by one. */
  Crown = 9,
  /** Skull King / Matriarch: calls small fry. */
  Summon = 10,
  /** Colossus: lobbed acid globs that burst into slow puddles of shot. */
  Acid = 11,
  /** Matriarch: screech shockwave that pushes the wizard back. */
  Screech = 12,
  COUNT,
}

const T = BossPattern;

export const BOSSES: readonly BossDef[] = [
  {
    kind: BossKind.Lich,
    name: 'VORMUND THE UNBURIED',
    title: 'LICH ARCHON',
    sprite: SpriteId.BossLich,
    voiceGroup: 'boss_arrive_lich',
    boltTint: 0xc07aff,
    orbTint: 0x7af6ff,
    wispTint: 0x9ad6ff,
    glowTint: 0xb04cff,
    stainTint: 0x2a1450,
    spriteTint: 0xffffff,
    archmage: true,
    body: BossBody.Arcane,
    entrance: BossEntrance.Blink,
    motion: BossMotion.Hover,
    scale: 2.9,
    radius: 1.05,
    lift: 0.35,
    speed: 1.6,
    hpMult: 1,
    patterns: [[T.Radial, T.Aimed], [T.Radial, T.Aimed, T.Orbit], [T.Radial, T.Aimed, T.Orbit, T.RingGap, T.Wisps, T.Beam]],
    minion: -1,
    theme: 0,
  },
  {
    kind: BossKind.Druid,
    name: 'SYLVARA THE THORNQUEEN',
    title: 'VERDANT HIEROPHANT',
    sprite: SpriteId.BossDruid,
    voiceGroup: 'boss_arrive_druid',
    boltTint: 0x8dff5a,
    orbTint: 0xffd23a,
    wispTint: 0xb6ff8a,
    glowTint: 0x5cff9a,
    stainTint: 0x1d3a16,
    spriteTint: 0xffffff,
    archmage: true,
    body: BossBody.Arcane,
    entrance: BossEntrance.Blink,
    motion: BossMotion.Hover,
    scale: 2.9,
    radius: 1.05,
    lift: 0.35,
    speed: 1.6,
    hpMult: 1,
    patterns: [[T.Radial, T.Aimed], [T.Radial, T.Aimed, T.Orbit], [T.Radial, T.Aimed, T.Orbit, T.RingGap, T.Wisps, T.Beam]],
    minion: -1,
    theme: 1,
  },
  {
    kind: BossKind.Magus,
    name: 'IGNATIUS EMBERCROWN',
    title: 'EMBER MAGUS',
    sprite: SpriteId.BossMagus,
    voiceGroup: 'boss_arrive_magus',
    boltTint: 0xffa040,
    orbTint: 0xff5a3a,
    wispTint: 0xffd070,
    glowTint: 0xff7a2a,
    stainTint: 0x3a1608,
    spriteTint: 0xffffff,
    archmage: true,
    body: BossBody.Arcane,
    entrance: BossEntrance.Blink,
    motion: BossMotion.Hover,
    scale: 2.9,
    radius: 1.05,
    lift: 0.35,
    speed: 1.6,
    hpMult: 1,
    patterns: [[T.Radial, T.Aimed], [T.Radial, T.Aimed, T.Orbit], [T.Radial, T.Aimed, T.Orbit, T.RingGap, T.Wisps, T.Beam]],
    minion: -1,
    theme: 2,
  },
  {
    kind: BossKind.Warlord,
    name: 'KHARGOTH THE UNBROKEN',
    title: 'GOLEM WARLORD',
    sprite: SpriteId.BossWarlord,
    voiceGroup: 'boss_arrive_warlord',
    boltTint: 0xffb060,
    orbTint: 0xd8a070,
    wispTint: 0xffc080,
    glowTint: 0xff8a2a,
    stainTint: 0x3a2a1c,
    spriteTint: 0xffffff,
    archmage: false,
    body: BossBody.Stone,
    entrance: BossEntrance.Crash,
    motion: BossMotion.Walk,
    scale: 3.2,
    radius: 1.25,
    lift: 0,
    speed: 1.7,
    hpMult: 1.15,
    patterns: [[T.Stomp, T.Rubble], [T.Stomp, T.Rubble, T.Charge], [T.Stomp, T.Rubble, T.Charge, T.Radial]],
    minion: EnemyKind.Golem,
    theme: 0,
  },
  {
    kind: BossKind.SkullKing,
    name: 'MORDRAKE HOLLOWCROWN',
    title: 'SKULL KING',
    sprite: SpriteId.BossSkullKing,
    voiceGroup: 'boss_arrive_skullking',
    boltTint: 0x9af6ff,
    orbTint: 0xdaf0ff,
    wispTint: 0x7fffd4,
    glowTint: 0x4ce8ff,
    stainTint: 0x142a2a,
    spriteTint: 0xffffff,
    archmage: false,
    body: BossBody.Bone,
    entrance: BossEntrance.Blink,
    motion: BossMotion.Hover,
    scale: 2.6,
    radius: 1.0,
    lift: 0.5,
    speed: 2.1,
    hpMult: 0.9,
    patterns: [[T.Crown, T.Aimed], [T.Crown, T.Aimed, T.Summon], [T.Crown, T.Aimed, T.Summon, T.Wisps]],
    minion: EnemyKind.Skull,
    theme: 0,
  },
  {
    kind: BossKind.Colossus,
    name: 'GLORP THE UNDIVIDED',
    title: 'SLIME COLOSSUS',
    sprite: SpriteId.BossColossus,
    voiceGroup: 'boss_arrive_colossus',
    boltTint: 0x9aff4a,
    orbTint: 0xc8ff6a,
    wispTint: 0xb0ff80,
    glowTint: 0x6aff3a,
    stainTint: 0x1e3a12,
    spriteTint: 0xc8ffb0,
    archmage: false,
    body: BossBody.Goo,
    entrance: BossEntrance.Rise,
    motion: BossMotion.Hop,
    scale: 3.4,
    radius: 1.3,
    lift: 0,
    speed: 3.2,
    hpMult: 1.25,
    patterns: [[T.Acid, T.Radial], [T.Acid, T.Radial, T.RingGap], [T.Acid, T.Radial, T.RingGap, T.Wisps]],
    minion: EnemyKind.Slime,
    theme: 1,
  },
  {
    kind: BossKind.Matriarch,
    name: 'VESPERA NIGHTBROOD',
    title: 'BAT MATRIARCH',
    sprite: SpriteId.BossMatriarch,
    voiceGroup: 'boss_arrive_matriarch',
    boltTint: 0xff6ab0,
    orbTint: 0xc060ff,
    wispTint: 0xff9ad0,
    glowTint: 0xe040c0,
    stainTint: 0x2c1030,
    spriteTint: 0xffb8d8,
    archmage: false,
    body: BossBody.Leather,
    entrance: BossEntrance.Swoop,
    motion: BossMotion.Flyer,
    scale: 2.8,
    radius: 1.0,
    lift: 0.7,
    speed: 4.2,
    hpMult: 0.8,
    patterns: [[T.Screech, T.Charge], [T.Screech, T.Charge, T.Summon], [T.Screech, T.Charge, T.Summon, T.Aimed]],
    minion: EnemyKind.Bat,
    theme: 1,
  },
  {
    kind: BossKind.Sentinel,
    name: 'THE WATCHER OF THE NINTH GATE',
    title: 'GARGOYLE SENTINEL',
    sprite: SpriteId.BossSentinel,
    voiceGroup: 'boss_arrive_sentinel',
    boltTint: 0xffc040,
    orbTint: 0xff8040,
    wispTint: 0xffd890,
    glowTint: 0xffa020,
    stainTint: 0x3a2410,
    spriteTint: 0xffe0b0,
    archmage: false,
    body: BossBody.Stone,
    entrance: BossEntrance.Rise,
    motion: BossMotion.Static,
    scale: 3.0,
    radius: 1.15,
    lift: 0,
    speed: 0,
    hpMult: 1.1,
    patterns: [[T.Aimed, T.Beam], [T.Aimed, T.Beam, T.Radial], [T.Aimed, T.Beam, T.Radial, T.RingGap]],
    minion: -1,
    theme: 2,
  },
  // Later champions. Each is a small-fry sheet scaled up and tinted, with a spell set
  // recombined from the existing patterns and its own kin as the summon.
  {
    kind: BossKind.Hexweaver,
    name: 'QUILLIX THE HEXWEAVER',
    title: 'IMP HEXWEAVER',
    sprite: SpriteId.BossHexweaver,
    voiceGroup: 'boss_arrive_hexweaver',
    boltTint: 0x5ff2ff,
    orbTint: 0x9a6cff,
    wispTint: 0x8af8ff,
    glowTint: 0x3fe0ff,
    stainTint: 0x142838,
    spriteTint: 0x9cf4ff,
    archmage: false,
    body: BossBody.Arcane,
    entrance: BossEntrance.Blink,
    motion: BossMotion.Hover,
    scale: 2.5,
    radius: 0.95,
    lift: 0.3,
    speed: 2.6,
    hpMult: 0.85,
    patterns: [[T.Aimed, T.Orbit], [T.Aimed, T.Orbit, T.Summon], [T.Aimed, T.Orbit, T.Summon, T.Beam]],
    minion: EnemyKind.Hexer,
    theme: 0,
    debutFloor: 2,
  },
  {
    kind: BossKind.Ashmaw,
    name: 'ASHMAW THE UNQUENCHED',
    title: 'CINDER TITAN',
    sprite: SpriteId.BossAshmaw,
    voiceGroup: 'boss_arrive_ashmaw',
    boltTint: 0xff8a30,
    orbTint: 0xffc040,
    wispTint: 0xffb060,
    glowTint: 0xff6a20,
    stainTint: 0x2a1006,
    spriteTint: 0xffa060,
    archmage: false,
    body: BossBody.Bone,
    entrance: BossEntrance.Crash,
    motion: BossMotion.Flyer,
    scale: 3.1,
    radius: 1.15,
    lift: 0.45,
    speed: 3.4,
    hpMult: 1.05,
    patterns: [[T.Charge, T.Radial], [T.Charge, T.Radial, T.Summon], [T.Charge, T.Radial, T.Summon, T.RingGap]],
    minion: EnemyKind.Cinder,
    theme: 2,
    debutFloor: 3,
  },
  {
    kind: BossKind.Bride,
    name: 'THE HOLLOW BRIDE',
    title: 'WRAITH MATRON',
    sprite: SpriteId.BossBride,
    voiceGroup: 'boss_arrive_bride',
    boltTint: 0xc8d8ff,
    orbTint: 0x8aa0ff,
    wispTint: 0xe0ecff,
    glowTint: 0xa8c0ff,
    stainTint: 0x18203a,
    spriteTint: 0xd0dcff,
    archmage: false,
    body: BossBody.Arcane,
    entrance: BossEntrance.Blink,
    motion: BossMotion.Hover,
    scale: 3.0,
    radius: 1.0,
    lift: 0.5,
    speed: 2.2,
    hpMult: 0.9,
    patterns: [[T.Wisps, T.Aimed], [T.Wisps, T.Aimed, T.Summon], [T.Wisps, T.Aimed, T.Summon, T.RingGap]],
    minion: EnemyKind.Wraith,
    theme: 1,
    debutFloor: 3,
  },
];

export function bossDef(kind: number): BossDef {
  return BOSSES[kind] ?? BOSSES[0];
}

/** Floor 1 is the lich, then the archmages rotate. */
export function bossKindForFloor(floor: number): BossKind {
  return (((Math.max(1, floor) - 1) % ARCHMAGE_COUNT) + ARCHMAGE_COUNT) % ARCHMAGE_COUNT;
}

/** Floor theme index (matches BossDef.theme) for a floor number. */
export function floorTheme(floor: number): number {
  return bossKindForFloor(floor);
}

/**
 * Picks the champion for a regular fight room: rotates through the roster with a bias toward
 * the floor's theme, never repeating the previous boss. The very first room of a run always
 * gets the Warlord so the opening boss lands with the heaviest entrance.
 */
export function pickChampion(rng: Rng, floor: number, roomIndex: number, previous: BossKind | -1): BossKind {
  if (roomIndex <= 1) return BossKind.Warlord;
  const theme = floorTheme(floor);
  let total = 0;
  const weights: number[] = [];
  for (let k = ARCHMAGE_COUNT; k < BossKind.COUNT; k++) {
    const def = BOSSES[k];
    const debut = def.debutFloor ?? 1;
    let w = k === previous || floor < debut ? 0 : def.theme === theme ? 3 : 1.4;
    // Sentinel is a stationary breather; keep it a little rarer.
    if (k === BossKind.Sentinel) w *= 0.7;
    // A champion's debut floor leans on it so the new face actually shows up.
    if (w > 0 && debut > 1 && floor === debut) w *= 1.6;
    weights.push(w);
    total += w;
  }
  let roll = rng.next() * total;
  for (let i = 0; i < weights.length; i++) {
    roll -= weights[i];
    if (roll <= 0) return (ARCHMAGE_COUNT + i) as BossKind;
  }
  return BossKind.Warlord;
}

/** Everything about a boss that changes with the floor / room number. */
export interface BossTuning {
  floor: number;
  room: number;
  hp: number;
  boltSpeed: number;
  boltDamage: number;
  rest: number;
  patterns: readonly BossPattern[];
  /** Extra shots per volley over the baseline. */
  bonusShots: number;
  /** Repeat count for multi-volley patterns. */
  volleys: number;
  /** Escalation tier 0..2. */
  tier: number;
  score: number;
  arriveSeconds: number;
  deathSeconds: number;
  contactDamage: number;
}

/** Escalation tier from floor (archmages) or global room index (champions). */
export function escalationTier(archmage: boolean, floor: number, room: number): number {
  if (archmage) return floor <= 1 ? 0 : floor === 2 ? 1 : 2;
  const e = Config.boss.escalation;
  return room >= e.tier3Room ? 2 : room >= e.tier2Room ? 1 : 0;
}

/**
 * Archmages scale with the floor, champions with the global room counter so a long run keeps
 * getting meaner. Start-screen difficulty applies on top (hp in spawnBoss, damage in dealDamage).
 */
export function bossTuning(kind: number, floor: number, room: number, hpMult = 1): BossTuning {
  const def = bossDef(kind);
  const c = Config.boss;
  const d = difficulty();
  const f = Math.max(1, floor);
  const r = Math.max(1, room);
  const tier = escalationTier(def.archmage, f, r);
  const extraFloor = f - 1;
  const extraRoom = r - 1;
  const patterns = def.patterns[Math.min(tier, def.patterns.length - 1)];
  if (def.archmage) {
    return {
      floor: f,
      room: r,
      tier,
      hp: Math.round(c.hp * (1 + c.hpPerFloor * extraFloor) * def.hpMult * hpMult),
      boltSpeed: Math.min(c.boltSpeedMax, c.boltSpeed + c.boltSpeedPerFloor * extraFloor) * d.projectileSpeed,
      boltDamage: Math.min(c.boltDamageMax, c.boltDamage + c.boltDamagePerFloor * extraFloor),
      rest: Math.max(c.restMin, c.restSeconds - c.restPerFloor * extraFloor) * d.bossRest,
      patterns,
      bonusShots: Math.min(6, extraFloor),
      volleys: f >= 4 ? 3 : 2,
      score: c.score,
      arriveSeconds: c.arriveSeconds,
      deathSeconds: c.deathSeconds,
      contactDamage: c.contactDamage,
    };
  }
  const ch = c.champion;
  const growth = Math.min(ch.hpGrowthMax, 1 + ch.hpPerRoom * extraRoom);
  return {
    floor: f,
    room: r,
    tier,
    hp: Math.round(ch.hp * growth * def.hpMult * hpMult),
    // Champion shots creep up by room, a little slower than the archmage's per-floor ramp.
    boltSpeed: Math.min(c.boltSpeedMax, c.boltSpeed * 0.9 + 0.22 * extraRoom) * d.projectileSpeed,
    boltDamage: Math.min(c.boltDamageMax, c.boltDamage - 1 + 0.5 * extraRoom),
    rest: Math.max(ch.restMin, ch.restSeconds - ch.restPerRoom * extraRoom) * d.bossRest,
    patterns,
    bonusShots: Math.min(5, Math.floor(extraRoom / 3)),
    volleys: tier >= 2 ? 3 : 2,
    score: Math.round(ch.score * (1 + 0.1 * extraRoom)),
    arriveSeconds: ch.arriveSeconds,
    deathSeconds: ch.deathSeconds,
    contactDamage: ch.contactDamage,
  };
}
